// src/SetupWizard/Step7Audience.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';

function Step7Audience({ targetAudience, onChange }) {
  const [selectedAudiences, setSelectedAudiences] = useState(
    targetAudience ? targetAudience.split(' + ') : []
  );
  const [customInput, setCustomInput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Validate initial state
    validateAndUpdate(selectedAudiences);
  }, []);

  const validateAndUpdate = (audiences) => {
    if (!audiences.length) {
      setError('Please select or enter at least one target audience');
      onChange({ targetAudience: audiences.join(' + '), isValid: false });
    } else {
      setError('');
      onChange({ targetAudience: audiences.join(' + '), isValid: true });
    }
  };

  const commonAudiences = [
    {
      category: "Experience",
      options: ["Beginners", "Intermediate", "Advanced", "All Levels"]
    },
    {
      category: "Demographics",
      options: ["Young Adults", "Parents", "Professionals", "Retirees"]
    },
    {
      category: "Interest",
      options: ["Hobbyists", "Enthusiasts", "Experts", "Casual"]
    }
  ];

  const handleQuickSelect = (audience) => {
    let newAudiences;
    if (selectedAudiences.includes(audience)) {
      newAudiences = selectedAudiences.filter(a => a !== audience);
    } else if (selectedAudiences.length < 3) {
      newAudiences = [...selectedAudiences, audience];
    } else {
      return; // Don't add if already at limit
    }
    setSelectedAudiences(newAudiences);
    validateAndUpdate(newAudiences);
  };

  const handleCustomInputChange = (e) => {
    setCustomInput(e.target.value);
    if (e.target.value) {
      const audiences = e.target.value.split(' + ').filter(a => a.trim());
      if (audiences.length <= 3) {
        setSelectedAudiences(audiences);
        validateAndUpdate(audiences);
      }
    } else {
      setSelectedAudiences([]);
      validateAndUpdate([]);
    }
  };

  const handleDelete = (audienceToDelete) => {
    const newAudiences = selectedAudiences.filter(a => a !== audienceToDelete);
    setSelectedAudiences(newAudiences);
    setCustomInput(newAudiences.join(' + '));
    validateAndUpdate(newAudiences);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 7: Define Your Target Audience
      </Typography>

      {/* Selected Audiences Display */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Selected Audiences ({selectedAudiences.length}/3):
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {selectedAudiences.length > 0 ? (
            selectedAudiences.map((audience) => (
              <Chip
                key={audience}
                label={audience}
                onDelete={() => handleDelete(audience)}
                color="primary"
                deleteIcon={<CancelIcon />}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No audiences selected
            </Typography>
          )}
        </Stack>
        <TextField
          fullWidth
          value={customInput}
          onChange={handleCustomInputChange}
          placeholder="Or type custom audiences (separate with ' + ')"
          helperText="Example: Tech Enthusiasts + Working Parents + Beginners"
          size="small"
          error={!!error}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Quick Selection Options in a more compact layout */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Select Options:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {commonAudiences.map((category) => (
            <Box key={category.category} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 80 }}>
                {category.category}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {category.options.map((option) => (
                  <Chip
                    key={option}
                    label={option}
                    onClick={() => handleQuickSelect(option)}
                    variant={selectedAudiences.includes(option) ? "filled" : "outlined"}
                    color={selectedAudiences.includes(option) ? "primary" : "default"}
                    size="small"
                    sx={{ 
                      cursor: selectedAudiences.length >= 3 && !selectedAudiences.includes(option) ? 'not-allowed' : 'pointer',
                      opacity: selectedAudiences.length >= 3 && !selectedAudiences.includes(option) ? 0.5 : 1,
                      '&:hover': {
                        transform: selectedAudiences.length >= 3 && !selectedAudiences.includes(option) ? 'none' : 'scale(1.05)'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Tips Section - More compact */}
      <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
          💡 Tips: Consider knowledge level • Focus on goals and pain points • Include demographics if relevant • Be specific but not restrictive
        </Typography>
      </Paper>
    </Box>
  );
}

export default Step7Audience;
